/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/**
 * @author Granite Bagas
 */
import React, { useMemo } from 'react';

import { View, TouchableOpacity } from 'react-native';
import { FontAwesome5 } from '@expo/vector-icons';
import { useDispatch } from 'react-redux';

import Table from '@components/table/Table';
import Toggle from '@components/form/Toggle';
import TitleSubSection from '@components/global/TitleSubSection';
import * as Layout from '@components/screen/Layout';
import Input from '@components/form/Input';
import SociollaText from '@components/sociolla/SociollaText';

import { showAlert } from '@/store/global';

const TaxesForm = ({
  title = '',
  values = {},
  id = '',
  errors = {},
  setFieldValue = () => {},
  handleToggle = () => {},
  isEdit = false,
}) => {
  const dispatch = useDispatch();

  const tempTaxData = useMemo(() => {
    return (values[id] || []).filter((v) => {
      if (v.is_deleted) {
        v.style = {
          display: 'none',
        };
      }

      return v;
    });
  }, [values[id]]);

  const tableStructure = {
    tax_value: {
      head: title,
      body: 'tax_value',
      type: 'slot',
    },
    cerebro_id: {
      head: 'Tax ID Cerebro',
      body: 'cerebro_id',
      type: 'slot',
    },
    description: {
      head: 'Description',
      body: 'description',
      type: 'slot',
    },
    default: {
      head: 'Default',
      body: 'default',
      type: 'slot',
    },
    ...(isEdit
      ? {
          action: {
            head: 'Action',
            body: 'action',
            type: 'slot',
          },
        }
      : {}),
  };

  const confirmDelete = (index) => {
    dispatch(
      showAlert({
        show: true,
        title: 'Delete Row',
        description: 'This whole row will be deleted. Do you want to proceed?',
        onCallback: () => {
          const updatedTax = values[id].map((v, i) => {
            if (i == index) {
              v['is_deleted'] = true;
            }
            return v;
          });
          setFieldValue(id, updatedTax);
        },
      }),
    );
  };

  const confirmDefault = (index, val) => {
    dispatch(
      showAlert({
        show: true,
        title: 'Confirm Default Status',
        description:
          'Are you sure you want to change the default status of this item?',
        onCallback: () => {
          const tempData = [...values[id]];
          tempData[index] = {
            ...tempData[index],
            is_default: val,
          };
          setFieldValue(id, tempData);

          handleToggle(index, val);
        },
      }),
    );
  };

  return (
    <View style={{ marginBottom: 20 }}>
      <TitleSubSection
        title={title.toUpperCase()}
        button={
          isEdit
            ? {
                label: 'Add More',
                callback: () => {
                  const newTax = [
                    ...values[id],
                    {
                      tax_value: '',
                      cerebro_id: '',
                      is_default: false,
                      is_deleted: false,
                    },
                  ];
                  setFieldValue(id, newTax);
                },
              }
            : null
        }
      />
      <Layout.FormWrapper>
        <Table
          structures={tableStructure}
          data={tempTaxData}
          withFetch={false}
          hasNoHide={true}
          showFilter={false}
          noTopSection={true}
          showPagination={false}
          withoutShadow={true}
          totalData={tempTaxData?.length || 0}
          limit={tempTaxData?.length || 7}
          overflowX={true}
          slot={{
            tax_value: ({ data, index }) => (
              <View style={{ display: 'table-caption' }} key={index}>
                {isEdit ? (
                  <Input
                    isTransparent={true}
                    value={data.tax_value}
                    isRequired
                    placeholder={title}
                    onChangeText={(text) => {
                      const tempData = [...values[id]];
                      tempData[index] = {
                        ...tempData[index],
                        tax_value: text,
                      };
                      setFieldValue(id, tempData);
                    }}
                    keyboardType="numeric"
                    isDecimal={true}
                    hasError={
                      errors[id]?.[index]?.tax_value
                        ? errors[id][index].tax_value
                        : null
                    }
                    rightIcon={<SociollaText>%</SociollaText>}
                  />
                ) : (
                  <SociollaText>{data.tax_value}%</SociollaText>
                )}
              </View>
            ),
            cerebro_id: ({ data, index }) => (
              <View style={{ display: 'table-caption' }} key={index}>
                {isEdit ? (
                  <Input
                    value={data.cerebro_id}
                    isRequired
                    placeholder="Tax ID Cerebro"
                    onChangeText={(text) => {
                      const tempData = [...values[id]];
                      tempData[index] = {
                        ...tempData[index],
                        cerebro_id: text,
                      };
                      setFieldValue(id, tempData);
                    }}
                    isNumeric={true}
                    hasError={
                      errors[id]?.[index]?.cerebro_id
                        ? errors[id][index].cerebro_id
                        : null
                    }
                    isTransparent={true}
                  />
                ) : (
                  <SociollaText>{data.cerebro_id}</SociollaText>
                )}
              </View>
            ),
            description: ({ data, index }) => (
              <View key={index}>
                {isEdit ? (
                  <Input
                    value={data.description}
                    isRequired
                    placeholder="Description"
                    onChangeText={(text) => {
                      const tempData = [...values[id]];
                      tempData[index] = {
                        ...tempData[index],
                        description: text,
                      };
                      setFieldValue(id, tempData);
                    }}
                    hasError={
                      errors[id]?.[index]?.description
                        ? errors[id][index].description
                        : null
                    }
                    isTransparent={true}
                  />
                ) : (
                  <SociollaText>{data.description}</SociollaText>
                )}
              </View>
            ),
            default: ({ data, index }) => (
              <View
                style={{ display: 'flex', flexDirection: 'row' }}
                key={index}>
                <Toggle
                  value={!!data.is_default}
                  size={'small'}
                  onValueChange={(val) => {
                    confirmDefault(index, val);
                  }}
                />
                {!isEdit && (
                  <SociollaText style={{ marginLeft: 5 }}>
                    {data.is_default ? 'Default' : 'Set as Default'}
                  </SociollaText>
                )}
              </View>
            ),
            action: ({ data, index }) => (
              <View key={`action-${index}`}>
                <TouchableOpacity
                  activeOpacity={0.8}
                  onPress={() => {
                    confirmDelete(index);
                  }}>
                  <FontAwesome5 name="trash" size={15} color="black" />
                </TouchableOpacity>
              </View>
            ),
          }}
        />
      </Layout.FormWrapper>
    </View>
  );
};

export default TaxesForm;
